import { defineComponent as _defineComponent } from 'vue'
import { UpcomingRelease } from '@/@types/graphql-types';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/features/buybox/graphql/queries/GetBuyBoxOffers.query';
import { excludePackages } from '@/constants/buybox-excluded-packages.constant';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useInternationalOffersTracking } from '@/features/international-offers/tracking';
import { useQuery } from '@/helpers/composables/useApollo';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useConstantStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';
import { timestamp } from '@/helpers/date-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/user.store';
import { until } from '@vueuse/core';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { trackFreeTrialSwiperExp } from '@/components/experiments/FreeTrialSwiperExp/tracking';
import { BuyBoxOffers, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { buyboxFilterByMonetizationType } from '@/features/buybox/helpers/list-buybox-config';

// need to import this immediately because it's above the fold and could cause CLS
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import RegularListBuyBox from '@/features/buybox/components/list/RegularListBuyBox.vue';
import ListBuyBoxFilter from '@/features/buybox/components/list/ListBuyBoxFilter.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import ListUpcomingNoOffers from '@/features/buybox/components/list/ListUpcomingNoOffers.vue';
import PromotedListBuyBox from '@/features/buybox/components/list/PromotedListBuyBox.vue';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { INTERNATIONAL_OFFERS_ENABLED_COUNTRIES } from '@/features/international-offers/international-offers-constants';
import { useActivateExperiment } from '@/libraries/experiment';
import { getPrimeShortName } from '@/helpers/promotion-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'ListBuyBox',
  props: {
    title: null,
    episode: null,
    buyboxOffers: { default: undefined },
    sponsoredAd: { default: undefined },
    buyBoxSwitchType: null,
    hideBuyBoxSwitcher: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    episodeNumber: null,
    placement: { default: 'regular_buybox' },
    upcomingEpisodeReleases: { default: null }
  },
  emits: {
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
},
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, episode?: EpisodeFragment, buyboxOffers: BuyBoxOffers | null, sponsoredAd: SponsoredAdFragment | undefined, buyBoxSwitchType?: NewBuyBoxView, hideBuyBoxSwitcher: boolean, additionalContexts: SnowplowContext[], episodeNumber?: number | null, placement: ClickoutPlacement, upcomingEpisodeReleases: UpcomingRelease[] | null };

const OfferReporter = () => import('@/features/buybox/components/OfferReporter.vue');
const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');
const BuyboxInternationalOffers = () =>
	import('@/features/international-offers/components/BuyboxInternationalOffers.vue');




const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);
const { setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn, isProUser } = useUserStore();
const { country, language } = useLanguageStore();
const { titleProviderCount } = useConstantStore();
const { t } = useI18n();
const { trackInternationalOffers } = useInternationalOffersTracking({ title: () => props.title });

useProvideClickoutPlacement(props.placement);

const buyboxContainer = ref<HTMLElement | null>(null);

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);

const { titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList } =
	useTitleDetailInfo(props.title);

// If the buybox is in the episodes list, use the episode id, objectId, objectType etc. instead of the title data
const buyBoxTitleId = computed(() => (props.episode ? props.episode.id : titleId.value));
const buyBoxObjectId = computed(() => (props.episode ? props.episode.objectId : titleObjectId.value));
const buyBoxObjectType = computed(() => (props.episode ? props.episode.objectType : titleObjectType.value));
const buyBoxIsUpcoming = computed(() => (props.episode ? !props.episode.content.isReleased : isTitleUpcoming.value));
const buyBoxSeasonNumber = computed(() => (props.episode ? props.episode.content.seasonNumber : seasonNumber.value));

const queryVariables = computed((): GetBuyBoxOffersQueryVariables => {
	return {
		nodeId: buyBoxTitleId.value ?? '',
		country: country.value,
		language: language.value,
		excludePackages: excludePackages(isLoggedIn.value, buyBoxTitleId.value, country.value, isProUser.value),
		fallbackToForeignOffers: INTERNATIONAL_OFFERS_ENABLED_COUNTRIES.includes(country.value),
	};
});

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables,
	() => ({ enabled: !props.buyboxOffers })
);
const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ loading, title: () => props.title });

// INT_OFFERS_REVENUE_CHECK_EXP
const isOffersRevenueCheckExpActive = computed(() => isInternationalOffersActive.value);
const { activeVariantWithControlGroup: intOffersRevenueCheckVariant } = useActivateExperiment(
	'INT_OFFERS_REVENUE_CHECK_EXP_V2',
	// checks for:
	// - has no domestic offers
	// - has no upcomingReleases
	isOffersRevenueCheckExpActive
);
// INT_OFFERS_REVENUE_CHECK_EXP

const buyboxData = computed(() => (!props.buyboxOffers ? (result.value?.node as BuyBoxOffers) : props.buyboxOffers));

const isEpisodeBuybox = computed(() => buyboxData.value?.__typename === 'Episode');
const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

const buyboxUpcomingReleases = computed(() => {
	return props.upcomingEpisodeReleases ?? props.title.content.upcomingReleases;
});

const buyBoxFilterOptions = computed(() => {
	return Object.keys(buyboxFilterByMonetizationType).filter(monetizationType =>
		allRegularOffers.value.some(offer =>
			buyboxFilterByMonetizationType[monetizationType as keyof typeof buyboxFilterByMonetizationType].includes(
				offer.monetizationType
			)
		)
	) as BuyBoxFilterOptions[];
});

const titleContext = computed(() => {
	const title = props.title.__typename === 'Season' ? props.title.show : props.title;
	return new SnowplowTitleContextGraphql(
		title.objectId,
		title.objectType,
		buyBoxSeasonNumber.value ?? null,
		props.episodeNumber ?? null
	);
});

provide('titleContext', titleContext);
provide('titleSourceId', buyBoxTitleId.value);
provide('filterOption', buyboxFilter);

const {
	filteredRegularOffers,
	allRegularOffers,
	upcomingReleases,
	upcomingReleasesFromGroups,
	promotedOffers,
	AVODOffer,
} = useBuyBoxData({
	title: () => props.title,
	buyboxData,
	buyboxFilter,
	buyboxUpcomingReleases,
});

const { inPromotionCatalogue, catalogueItem } = usePromotion({ title: props.title, offers: allRegularOffers });

// OVER_PROVIDER_EXP_V2
const isOverProviderActive = computed(() => {
	return (
		allRegularOffers.value.some(offer => offer.package.shortName === getPrimeShortName(country.value)) &&
		// exclude titles with Hulu or ATV+ free trial, for example
		['cru', 'prv', 'amp'].includes(catalogueItem.value?.shortName || '')
	);
});
useActivateExperiment('OVER_PROVIDER_EXP_V3', isOverProviderActive);
// OVER_PROVIDER_EXP_V2

const lastUpdated = computed(() => {
	if (
		buyboxData.value?.__typename === 'Episode' ||
		buyboxData.value?.__typename === 'Movie' ||
		buyboxData.value?.__typename === 'Season' ||
		buyboxData.value?.__typename === 'Show'
	) {
		return buyboxData.value.maxOfferUpdatedAt;
	}
	return null;
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

async function onNotifyMe() {
	if (props.title && buyBoxTitleId.value) {
		const title = props.title.__typename === 'Season' ? props.title.show : props.title;

		const updatedTitle = await setInWatchlist(
			title.id,
			isInWatchList.value,
			{
				title: title.content.title,
				objectType: title.objectType,
				objectId: title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}
}

function trackInternationalOffersImpression(isVisible: boolean) {
	if (isVisible) {
		trackInternationalOffers('impression');
	}
}

watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && buyBoxTitleId.value) {
			onIsLoggedInChange(
				buyBoxTitleId.value || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				isInWatchList.value,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	isEpisodeBuybox.value ? (episodeBuyBoxType.value = payload.value) : setNewBuyboxFilterType(payload.value);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);

	// FT_SWIPER_EXP
	if (!payload.nonInteraction && !inPromotionCatalogue.value) {
		trackFreeTrialSwiperExp({
			action: 'click',
			label: `filter_toggled_${payload.value}`,
		});
	}
	// FT_SWIPER_EXP
}

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);

	if (!AVODOffer.value) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: AVODOffer.value, title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}

// FT_SWIPER_EXP
const swipeIndex = ref<number | null>(null);
const setSwiperIndex = (index: number) => {
	swipeIndex.value = index;
};
provide('swipeIndex', swipeIndex);
provide('setSwiperIndex', setSwiperIndex);

onMounted(async () => {
	await until(result).toBeTruthy();
	if (!inPromotionCatalogue.value) {
		trackFreeTrialSwiperExp({
			action: 'impression',
		});
	}
});
// FT_SWIPER_EXP

return { __sfc: true,OfferReporter, PlexPlayerModal, BuyboxInternationalOffers, emits, props, setInWatchlist, onIsLoggedInChange, setNewBuyboxFilterType, newBuyboxFilterType, isLoggedIn, isProUser, country, language, titleProviderCount, t, trackInternationalOffers, buyboxContainer, episodeBuyBoxType, titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList, buyBoxTitleId, buyBoxObjectId, buyBoxObjectType, buyBoxIsUpcoming, buyBoxSeasonNumber, queryVariables, result, loading, isInternationalOffersActive, isOffersRevenueCheckExpActive, intOffersRevenueCheckVariant, buyboxData, isEpisodeBuybox, buyboxFilter, buyboxUpcomingReleases, buyBoxFilterOptions, titleContext, filteredRegularOffers, allRegularOffers, upcomingReleases, upcomingReleasesFromGroups, promotedOffers, AVODOffer, inPromotionCatalogue, catalogueItem, isOverProviderActive, lastUpdated, timestampText, onNotifyMe, trackInternationalOffersImpression, handleFilterClick, watchNowTitleClicked, swipeIndex, setSwiperIndex, mrcObservableOptions, BuyBoxCountrySwitcher, RegularListBuyBox, ListBuyBoxFilter, BuyBoxTypeSwitcher, ListUpcomingNoOffers, PromotedListBuyBox }
}

})